import { useCallback, useEffect, useState } from "react";
import type { ExpenseTaxDefaultDto, ExpenseTypeDto, ExpenseTypeInput, ExpenseTypeOptionsDto } from "@delta/shared";
import { useLang } from "../../i18n";
import { api } from "../../lib/api";
import { errorText, fieldErrorsOf } from "../../lib/errors";
import { Icon } from "../../components/Icon";
import { PageHead, Shell } from "../../components/Shell";
import { ListView, type Column } from "../../components/ListView";
import { Field, Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { LockedButton, lockTag } from "./common";
import { AccName, accText } from "../accounting/common";

// Settings › Expense types (migration 045, request of 29 Oct 2026, item 7). Each type has an English and an Arabic
// name, the expense account it posts to and an optional default cost center; people entering an expense choose the
// type, never the account. Only the business owner or a user granted journalEntryAdd may add or change types, since
// they link accounts. A type is deactivated, never deleted; expenses already saved keep their account and names.

function TypeForm({ x, opts, onClose, onSaved }: { x: ExpenseTypeDto | null; opts: ExpenseTypeOptionsDto | null; onClose(): void; onSaved(v: ExpenseTypeDto, created: boolean): void }) {
  const { t, L } = useLang();
  const [v, setV] = useState<ExpenseTypeInput>(() => (x ? { nameEn: x.nameEn, nameAr: x.nameAr, accountId: x.account.id, costCenterId: x.costCenter?.id ?? null, active: x.active }
    : { nameEn: "", nameAr: "", accountId: "", costCenterId: null, active: true }));
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [alert, setAlert] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof ExpenseTypeInput>(k: K, val: ExpenseTypeInput[K]) => { setV((o) => ({ ...o, [k]: val })); setErrs((e) => ({ ...e, [k]: "" })); };
  async function save() {
    const e: Record<string, string> = {};
    if (!v.nameEn.trim()) e.nameEn = t("requiredField");
    if (!v.nameAr.trim()) e.nameAr = t("requiredField");
    if (!v.accountId) e.accountId = t("requiredField");
    setErrs(e);
    if (Object.keys(e).length) { setAlert(Object.values(e)[0]!); return; }
    setBusy(true); setAlert(null);
    try { onSaved(x ? await api<ExpenseTypeDto>("PUT", `/settings/expense-types/${x.id}`, v) : await api<ExpenseTypeDto>("POST", "/settings/expense-types", v), !x); }
    catch (err) { setErrs(fieldErrorsOf(t, err)); setAlert(errorText(t, err)); }
    finally { setBusy(false); }
  }
  const moved = !!x && x.expensesCount > 0 && v.accountId !== x.account.id;
  return (
    <Modal title={t(x ? "etEdit" : "etNew")} sub={t("etFormSub")} onClose={onClose} onSubmit={() => void save()} alert={alert} busy={busy}>
      <Field label={t("etNameEn")} req error={errs.nameEn} htmlFor="et-en"><input id="et-en" className="control" dir="ltr" value={v.nameEn} maxLength={120} onChange={(e) => set("nameEn", e.target.value)} autoComplete="off" placeholder="Rent" /></Field>
      <Field label={t("etNameAr")} req error={errs.nameAr} htmlFor="et-ar"><input id="et-ar" className="control" dir="rtl" value={v.nameAr} maxLength={120} onChange={(e) => set("nameAr", e.target.value)} autoComplete="off" placeholder="إيجار" /></Field>
      <Field label={t("etAccount")} req full error={errs.accountId} htmlFor="et-acc" hint={moved ? t("etAccountMoved", { n: x!.expensesCount }) : t("etAccountHint")}>
        <select id="et-acc" className="control" value={v.accountId} disabled={!opts} onChange={(e) => set("accountId", e.target.value)}>
          <option value="">{t("choose")}</option>{(opts?.accounts ?? []).map((a) => <option key={a.id} value={a.id}>{a.code} {L(a)}</option>)}
        </select>
      </Field>
      <Field label={t("etCostCenter")} error={errs.costCenterId} htmlFor="et-cc" hint={t("etCostCenterHint")}>
        <select id="et-cc" className="control" value={v.costCenterId ?? ""} disabled={!opts} onChange={(e) => set("costCenterId", e.target.value || null)}>
          <option value="">–</option>{(opts?.costCenters ?? []).map((c) => <option key={c.id} value={c.id}>{L(c)}</option>)}
        </select>
      </Field>
      <Field label={t("status")} htmlFor="et-active" hint={v.active ? undefined : t("etInactiveHint")}>
        <label className="check-row"><input type="checkbox" id="et-active" checked={v.active} onChange={(e) => set("active", e.target.checked)} /> {t("etActive")}</label>
      </Field>
    </Modal>
  );
}

/**
 * Migration 056 (request of 31 Oct 2026): the business default for whether an expense's input tax is recoverable.
 * Ordinary users are not asked; their expenses take this. Not set: an expense with tax needs an accountant's choice.
 */
function TaxDefault() {
  const { t } = useLang();
  const toast = useToast();
  const [x, setX] = useState<ExpenseTaxDefaultDto | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api<ExpenseTaxDefaultDto>("GET", "/settings/expense-tax-default").then(setX, () => setX(null)); }, []);
  if (!x) return null;
  const val = x.recoverable === null ? "" : x.recoverable ? "yes" : "no";
  const change = async (v: string) => {
    setBusy(true);
    try { setX(await api<ExpenseTaxDefaultDto>("PUT", "/settings/expense-tax-default", { recoverable: v === "" ? null : v === "yes" })); toast(t("etTaxDefaultSaved")); }
    catch (e) { toast(errorText(t, e)); } finally { setBusy(false); }
  };
  return (
    <section className="card" data-testid="et-tax-default" style={{ padding: 16, marginBottom: 16 }}>
      <Field label={t("etTaxDefault")} htmlFor="et-tax-default" hint={t(x.recoverable === null ? "etTaxDefaultUnset" : "etTaxDefaultHint")}>
        <select id="et-tax-default" className="control" value={val} disabled={!x.canChange || busy} onChange={(e) => void change(e.target.value)} title={x.canChange ? undefined : t("etManageLocked")}>
          <option value="">{t("etTaxDefaultNone")}</option><option value="yes">{t("expRecYesPlain")}</option><option value="no">{t("expRecNo")}</option>
        </select>
      </Field>
    </section>
  );
}

export function ExpenseTypes() {
  const { t, L } = useLang();
  const toast = useToast();
  const [rows, setRows] = useState<ExpenseTypeDto[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [opts, setOpts] = useState<ExpenseTypeOptionsDto | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<ExpenseTypeDto | "new" | null>(null);
  useEffect(() => { api<ExpenseTypeOptionsDto>("GET", "/settings/expense-types/options").then(setOpts, () => setOpts(null)); }, []);
  const load = useCallback(async () => {
    setFailed(null);
    const p = new URLSearchParams();
    if (filters.name) p.set("q", filters.name);
    try { setRows(await api<ExpenseTypeDto[]>("GET", `/settings/expense-types${p.size ? "?" + p : ""}`)); } catch (e) { setFailed(errorText(t, e)); }
  }, [filters.name, t]);
  useEffect(() => { void load(); }, [load]);
  const can = !!opts?.canManage;
  const columns: Column<ExpenseTypeDto>[] = [
    { k: "name", label: t("etName"), cell: (x) => <b>{L(x)}</b>, csv: (x) => `${x.nameEn} / ${x.nameAr}` },
    { k: "acc", label: t("etAccount"), cell: (x) => <AccName a={x.account} />, csv: (x) => accText(L, x.account) },
    { k: "cc", label: t("etCostCenter"), cell: (x) => (x.costCenter ? L(x.costCenter) : <span className="muted">–</span>), csv: (x) => (x.costCenter ? L(x.costCenter) : "") },
    { k: "n", label: t("etUsed"), end: true, cell: (x) => <span className="num">{x.expensesCount}</span>, csv: (x) => String(x.expensesCount) },
    { k: "st", label: t("status"), cell: (x) => <span className={`pill ${x.active ? "good" : ""}`} data-testid="et-status">{t(x.active ? "etActive" : "etInactive")}</span>, csv: (x) => t(x.active ? "etActive" : "etInactive") }
  ];
  const lock = { title: t("etManageLocked") };
  const newBtn = can ? <button className="btn btn-primary" onClick={() => setEditing("new")} data-testid="et-new"><Icon name="plus" />{t("etNew")}</button>
    : <LockedButton label={t("etNew")} title={lock.title} />;
  return (
    <Shell navKey="/settings/expense-types">
      <PageHead crumbs={[[t("home"), "/home"], [t("settings")], [t("expenseTypes")]]} title={t("expenseTypes")} />
      <div className="dep-note" data-testid="et-intro"><Icon name="info" /><span>{t("etIntro")}</span></div>
      <TaxDefault />
      <ListView rows={rows} error={failed} columns={columns} file="expense-types" testId="et-table" onRefresh={() => void load()}
        search={[{ k: "name", label: t("etName"), type: "text" }]} filters={filters} onFilters={setFilters}
        quick={(x) => `${x.nameEn} ${x.nameAr} ${x.account.code}`} newButton={newBtn}
        empty={{ icon: "receipt", title: t("etEmpty"), body: t(can ? "etEmptyBody" : "etEmptyBodyAsk") }}
        actions={(x) => [{ label: t("edit"), icon: "edit", run: () => setEditing(x), ...(can ? {} : lockTag(t)) }]} />
      {editing && <TypeForm x={editing === "new" ? null : editing} opts={opts} onClose={() => setEditing(null)}
        onSaved={(x, created) => { setEditing(null); toast(t(created ? "addedOk" : "updatedOk", { x: L(x) })); void load(); }} />}
    </Shell>
  );
}
